import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';
import { showErrorToast } from '../../utils/toast';

const initialState = {
  settings: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  },
  loading: false,
  error: null,
  filters: {
    search: '',
    page: 1,
    pageSize: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
};

// Fetch settings data from the server
export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { filters } = getState().settings;
      
      // Include all filter parameters in query
      const queryParams = new URLSearchParams({
        page: filters.page.toString(),
        pageSize: filters.pageSize.toString(),
        search: filters.search || '',
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      });

      const response = await axiosInstance.get(`/config/settings?${queryParams}`);
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch settings';
      showErrorToast(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setFilter: (state, action) => {
      // Always reset to page 1 when search changes or when explicitly requested
      if (action.payload.search !== undefined || action.payload.page === 1) {
        state.filters.page = 1;
      }
      
      // Merge new filters with existing filters
      state.filters = { 
        ...state.filters, 
        ...action.payload 
      };
    },
    resetFilters: (state) => {
      state.filters = { ...initialState.filters };
      state.pagination = { ...initialState.pagination };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.loading = false;
        
        // Ensure all expected properties exist
        const payload = action.payload || {};
        state.settings = payload.settings || [];
        state.pagination = {
          currentPage: payload.currentPage || 1,
          totalPages: payload.totalPages || 1,
          totalItems: payload.totalItems || 0,
          itemsPerPage: payload.pageSize || state.filters.pageSize,
          hasNextPage: payload.totalPages > payload.currentPage,
          hasPrevPage: payload.currentPage > 1,
        };
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        
        // Reset settings and pagination on error
        state.settings = [];
        state.pagination = { ...initialState.pagination };
      });
  },
});

export const { setFilter, resetFilters } = settingsSlice.actions;
export default settingsSlice.reducer;